/**
 * WONDR EOY 2026 landing page (template page.wondr-eoy-2026).
 *
 * - Hero carousel (sections/pb-eoy-lp-hero.liquid): dots, swipe, arrow keys.
 *   A tap / Enter on the box toggles its contents image (hover does it in CSS).
 *   The scroll cue scrolls to the next section.
 * - Message video (sections/pb-eoy-lp-message.liquid): the play button starts
 *   the video and hands over to the native controls.
 * - Gift slider (sections/pb-eoy-lp-gifts.liquid): arrows scroll one card,
 *   dots follow the scroll position (one per screen of cards).
 * - Gift quiz (sections/pb-eoy-lp-quiz.liquid): six yes/no cards (buttons or
 *   swipe), the box with the most points shows with an add-to-cart button.
 * - Discovery (sections/pb-eoy-lp-discovery.liquid): tabs and scent cards
 *   switch panels; mini add-to-cart. The panels are <pb-eoy-discovery>.
 *
 * Every LP section loads this file; each part initialises once per element.
 */
(() => {
  // Every LP section renders pb-eoy-lp-head, so this script tag is on the page ~10 times; run it once.
  // Sections added later in the theme editor are picked up by the shopify:section:load listener below.
  if (window.PBEoyLp) return;

  const once = (el, key) => {
    if (el.dataset[key] === 'true') return false;
    el.dataset[key] = 'true';
    return true;
  };

  // Lazy images in a [hidden] element only start loading once it is shown; start them ahead of time.
  const warmImages = (el) => {
    el?.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
  };

  function initHero(hero) {
    if (!once(hero, 'lpReady')) return;

    const slides = [...hero.querySelectorAll('[data-hero-slide]')];
    const labels = [...hero.querySelectorAll('[data-hero-label]')];
    const dots = [...hero.querySelectorAll('[data-hero-dot]')];
    let index = 0;

    const show = (next) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.hidden = i !== index;
        slide.setAttribute('aria-pressed', 'false');
        if (i === index) slide.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
      });
      labels.forEach((label, i) => (label.hidden = i !== index));
      dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
    };

    // Set by a swipe, so the click that may follow it does not also toggle the box; reset by every new gesture.
    let swiped = false;

    slides.forEach((slide) => {
      slide.addEventListener('click', () => {
        if (swiped) {
          swiped = false;
          return;
        }
        // A click opens this giftbox in the discovery section (its tab selected, scrolled into view).
        // Without a discovery panel for it, the click toggles the contents photo as before.
        const handled = !document.dispatchEvent(
          new CustomEvent('pb-eoy-lp:show-box', { detail: { handle: slide.dataset.heroHandle }, cancelable: true })
        );
        if (handled || !slide.classList.contains('has-open')) return;
        slide.setAttribute('aria-pressed', slide.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
      });
      slide.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        show(index + (event.key === 'ArrowRight' ? 1 : -1));
        slides[index].focus();
      });
    });

    dots.forEach((dot, i) => dot.addEventListener('click', () => show(i)));
    hero.querySelector('[data-hero-prev]')?.addEventListener('click', () => show(index - 1));
    hero.querySelector('[data-hero-next]')?.addEventListener('click', () => show(index + 1));

    // Horizontal swipe on the box = next / previous slide (a tap stays a tap).
    const stage = hero.querySelector('.pb-eoy-lp-hero__stage');
    let start = null;
    stage?.addEventListener('pointerdown', (event) => {
      start = { x: event.clientX, y: event.clientY };
      swiped = false;
    });
    stage?.addEventListener('pointerup', (event) => {
      if (!start || slides.length < 2) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      start = null;
      if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      swiped = true;
      show(index + (dx < 0 ? 1 : -1));
    });
    stage?.addEventListener('pointercancel', () => (start = null));
    // A mouse drag would otherwise start a native image drag, which cancels the pointer gesture.
    stage?.addEventListener('dragstart', (event) => event.preventDefault());

    hero.querySelector('[data-hero-cue]')?.addEventListener('click', () => {
      const section = hero.closest('.shopify-section');
      const next = section?.nextElementSibling;
      next?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    });
  }

  function initVideo(wrap) {
    if (!once(wrap, 'lpReady')) return;
    const video = wrap.querySelector('video');
    const play = wrap.querySelector('[data-lp-video-play]');
    if (!video || !play) return;

    play.addEventListener('click', () => {
      wrap.classList.add('is-playing');
      video.controls = true;
      video.play()?.catch(() => {});
      video.focus();
    });
  }

  function initSlider(section) {
    if (!once(section, 'lpReady')) return;
    const track = section.querySelector('[data-lp-slider-track]');
    const prev = section.querySelector('[data-lp-slider-prev]');
    const next = section.querySelector('[data-lp-slider-next]');
    const dotsWrap = section.querySelector('[data-lp-slider-dots]');
    if (!track) return;

    const step = () => {
      const first = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return first ? first.getBoundingClientRect().width + gap : track.clientWidth;
    };
    const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduced() ? 'auto' : 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduced() ? 'auto' : 'smooth' }));

    const sync = () => {
      const max = track.scrollWidth - track.clientWidth;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;

      if (!dotsWrap) return;
      const pages = Math.max(1, Math.ceil(track.scrollWidth / track.clientWidth));
      if (dotsWrap.children.length !== pages) {
        dotsWrap.replaceChildren(...Array.from({ length: pages }, () => document.createElement('span')));
      }
      const active = max > 0 ? Math.round((track.scrollLeft / max) * (pages - 1)) : 0;
      [...dotsWrap.children].forEach((dot, i) => dot.classList.toggle('is-active', i === active));
    };

    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  }

  // Adds one item through PBCart. A failure (network, unavailable variant, no
  // PBCart) shows the flow's role="alert" line; the next try clears it.
  const addToCart = async (button, variantId, errorEl) => {
    if (button.disabled) return;
    const fail = (error) => {
      console.error('[EOY LP] add to cart failed', error);
      if (errorEl) errorEl.textContent = errorEl.dataset.text || '';
    };
    if (errorEl) errorEl.textContent = '';
    if (!window.PBCart?.addLine) return fail(new Error('PBCart.addLine not available'));
    button.disabled = true;
    try {
      await window.PBCart.addLine(variantId, 1);
    } catch (error) {
      fail(error);
    } finally {
      button.disabled = false;
    }
  };

  // Ties go to the earlier box: Comfort > Softness > Unwind (Figma dev note 1412:6687).
  const QUIZ_ORDER = ['comfort', 'softness', 'unwind'];
  const quizWinner = (scores) =>
    QUIZ_ORDER.reduce((best, box) => ((scores[box] || 0) > (scores[best] || 0) ? box : best), QUIZ_ORDER[0]);

  function initQuiz(quiz) {
    if (!once(quiz, 'lpReady')) return;

    const cards = [...quiz.querySelectorAll('[data-quiz-card]')];
    const bars = [...quiz.querySelectorAll('[data-quiz-bar]')];
    const results = [...quiz.querySelectorAll('[data-quiz-result]')];
    const count = quiz.querySelector('[data-quiz-count]');
    const buttons = quiz.querySelector('[data-quiz-buttons]');
    const restart = quiz.querySelector('[data-quiz-restart]');
    const match = quiz.querySelector('[data-quiz-match]');
    const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
    let step = 0;
    let scores = {};
    let busy = false;

    const render = () => {
      const done = step >= cards.length;
      cards.forEach((card, i) => {
        const depth = i - step;
        card.hidden = depth < 0;
        card.dataset.depth = depth > 2 ? 'far' : String(depth);
        card.setAttribute('aria-hidden', depth === 0 ? 'false' : 'true');
        card.classList.remove('is-out-yes', 'is-out-no', 'is-dragging');
        card.style.removeProperty('--dx');
        card.style.removeProperty('--rot');
      });
      bars.forEach((bar, i) => bar.classList.toggle('is-on', i <= step));
      if (count) {
        count.textContent = done
          ? count.dataset.done
          : (quiz.dataset.counter || '').replace('[n]', step + 1).replace('[total]', cards.length);
      }
      const winner = done ? quizWinner(scores) : null;
      results.forEach((result) => (result.hidden = result.dataset.quizResult !== winner));
      quiz.classList.toggle('is-done', done);
      if (buttons) buttons.hidden = done;
      if (restart) restart.hidden = !done;
      if (match) match.hidden = done;
    };

    // Yes = +2 for the card's yes-box, no = +1 for its no-box.
    const answer = (yes) => {
      if (busy || step >= cards.length) return;
      const card = cards[step];
      const box = yes ? card.dataset.yes : card.dataset.no;
      if (step === 0) results.forEach(warmImages);
      scores[box] = (scores[box] || 0) + (yes ? 2 : 1);
      busy = true;
      card.classList.add(yes ? 'is-out-yes' : 'is-out-no');
      const next = () => {
        busy = false;
        step += 1;
        render();
        // The answer buttons are hidden now; move focus to the result instead of losing it to <body>.
        if (step >= cards.length) {
          const title = quiz.querySelector('[data-quiz-result]:not([hidden]) .pb-eoy-lp-quiz__result-title');
          if (title) {
            title.tabIndex = -1;
            title.focus({ preventScroll: true });
          }
        }
      };
      if (reduced()) next();
      else setTimeout(next, 280);
    };

    quiz.querySelectorAll('[data-quiz-answer]').forEach((button) => {
      button.addEventListener('click', () => answer(button.dataset.quizAnswer === 'yes'));
    });

    restart?.addEventListener('click', () => {
      step = 0;
      scores = {};
      render();
      quiz.querySelector('[data-quiz-answer="yes"]')?.focus();
    });

    // Drag the top card: far enough right = yes, left = no; otherwise it springs back.
    cards.forEach((card) => {
      let startX = null;
      card.addEventListener('pointerdown', (event) => {
        if (card.dataset.depth !== '0' || busy) return;
        startX = event.clientX;
        card.setPointerCapture(event.pointerId);
        card.classList.add('is-dragging');
      });
      card.addEventListener('pointermove', (event) => {
        if (startX === null) return;
        const dx = event.clientX - startX;
        card.style.setProperty('--dx', `${dx}px`);
        card.style.setProperty('--rot', `${dx / 20}deg`);
      });
      const end = (event) => {
        if (startX === null) return;
        const dx = event.clientX - startX;
        startX = null;
        card.classList.remove('is-dragging');
        card.style.removeProperty('--dx');
        card.style.removeProperty('--rot');
        if (event.type === 'pointerup' && Math.abs(dx) > 90) answer(dx > 0);
      };
      card.addEventListener('pointerup', end);
      card.addEventListener('pointercancel', end);
    });

    quiz.querySelectorAll('[data-quiz-atc]').forEach((button) => {
      button.addEventListener('click', () =>
        addToCart(button, button.dataset.quizAtc, button.parentElement.querySelector('[data-cart-error]')),
      );
    });

    render();
  }

  // LP discovery (sections/pb-eoy-lp-discovery.liquid): one panel per product.
  // A tab shows its panel (Kerstballen: the last chosen kerstbal); a scent
  // card shows that kerstbal. Focus follows to the same control in the new panel.
  // Every panel has its own copy of the tab strip and scent cards: the new
  // panel takes over the old strips' scroll position, then slides the chosen
  // tab / card fully into view. A tab click also scrolls the page down to the
  // image (and mini add-to-cart) when they are not fully on screen (mobile).
  function initDiscovery(root) {
    if (!once(root, 'lpReady')) return;

    const panels = [...root.querySelectorAll('[data-lp-dsc-panel]')];
    const STRIPS = ['.pb-eoy-lp-dsc__tabs', '.pb-eoy-lp-dsc__scent-track'];
    const behavior = () => (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');
    let kerstbal = panels.find((panel) => panel.dataset.lpDscKey === 'kerstballen')?.dataset.lpDscPanel;

    const visiblePanel = () => panels.find((panel) => !panel.hidden);

    // Scrolls a sideways strip just enough to show its pressed control whole.
    const revealPressed = (panel) => {
      for (const selector of STRIPS) {
        const strip = panel?.querySelector(selector);
        const item = strip?.querySelector('[aria-pressed="true"]');
        if (!item) continue;
        const box = strip.getBoundingClientRect();
        const rect = item.getBoundingClientRect();
        let delta = 0;
        // Scent cards: centre the chosen one so both neighbours peek out and stay clickable
        // (Margot 4/11: on desktop, with Cinnamon scrolled out at the left a mouse could not get back to it).
        if (selector === '.pb-eoy-lp-dsc__scent-track') delta = rect.left + rect.width / 2 - (box.left + box.width / 2);
        else if (rect.right > box.right) delta = rect.right - box.right + 16;
        else if (rect.left < box.left) delta = rect.left - box.left - 16;
        if (delta) strip.scrollBy({ left: delta, behavior: behavior() });
      }
    };

    // Page scroll so the image and the add-to-cart under it are on screen, below the fixed nav.
    const revealStage = (panel) => {
      const stage = panel?.querySelector('.pb-eoy-dsc__stage');
      if (!stage) return;
      const bottomEl = panel.querySelector('.pb-eoy-lp-dsc__atc') || stage;
      const navBottom = document.querySelector('.pb-unified-nav__bar')?.getBoundingClientRect().bottom || 0;
      const top = stage.getBoundingClientRect().top - (Math.max(navBottom, 0) + 12);
      const bottom = bottomEl.getBoundingClientRect().bottom + 16 - window.innerHeight;
      const delta = Math.min(bottom, top);
      if (delta > 0) window.scrollBy({ top: delta, behavior: behavior() });
    };

    const show = (handle, focusSelector) => {
      const target = panels.find((panel) => panel.dataset.lpDscPanel === handle);
      if (!target || !target.hidden) return;
      const current = visiblePanel();
      const offsets = STRIPS.map((selector) => current?.querySelector(selector)?.scrollLeft || 0);
      for (const panel of panels) panel.hidden = panel !== target;
      STRIPS.forEach((selector, i) => {
        const strip = target.querySelector(selector);
        if (strip) strip.scrollLeft = offsets[i];
      });
      if (target.dataset.lpDscKey === 'kerstballen') kerstbal = handle;
      if (focusSelector) target.querySelector(focusSelector)?.focus({ preventScroll: true });
    };

    root.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const tab = target?.closest('[data-lp-dsc-tab]');
      if (tab) {
        const key = tab.dataset.lpDscTab;
        show(key === 'kerstballen' ? kerstbal : key, `[data-lp-dsc-tab="${key}"]`);
        revealPressed(visiblePanel());
        revealStage(visiblePanel());
        return;
      }

      const scent = target?.closest('[data-lp-dsc-scent]');
      if (scent) {
        show(scent.dataset.lpDscScent, `[data-lp-dsc-scent="${scent.dataset.lpDscScent}"]`);
        revealPressed(visiblePanel());
        return;
      }

      const atc = target?.closest('[data-lp-dsc-atc]');
      if (atc) addToCart(atc, atc.dataset.lpDscAtc, atc.parentElement.querySelector('[data-cart-error]'));
    });

    // Hover / focus / touch on a tab or scent: load that panel's photos before the click.
    const warmPanel = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const tab = target?.closest('[data-lp-dsc-tab], [data-lp-dsc-scent]');
      if (!tab) return;
      const key = tab.dataset.lpDscTab;
      const handle = tab.dataset.lpDscScent || (key === 'kerstballen' ? kerstbal : key);
      warmImages(panels.find((panel) => panel.dataset.lpDscPanel === handle));
    };
    root.addEventListener('pointerover', warmPanel);
    root.addEventListener('focusin', warmPanel);

    // The hero asks for a giftbox (initHero): select its tab and bring the image + add to cart on screen.
    document.addEventListener('pb-eoy-lp:show-box', (event) => {
      // A section reload in the theme editor leaves this listener on a detached root; let the new one answer.
      if (!root.isConnected) return;
      const handle = event.detail?.handle;
      const panel = panels.find((p) => p.dataset.lpDscPanel === handle);
      if (!panel || event.defaultPrevented) return;
      event.preventDefault();
      warmImages(panel);
      const key = panel.dataset.lpDscKey === 'kerstballen' ? 'kerstballen' : handle;
      show(handle, `[data-lp-dsc-tab="${key}"]`);
      revealPressed(panel);
      revealStage(panel);
    });
  }

  // Momenten lightbox (Figma 1498:10474 / 1498:10570). The collage is the shared
  // sections/pb-eoy-collage.liquid; this file only loads on the LP, so the
  // lightbox only exists there. Photos = the tiles visible at the current width.
  function initLightbox(collage) {
    // .pb-eoy-col also matches the EOY collection page, which has no photo tiles: no empty dialog there.
    if (!collage.querySelector('.pb-eoy-col__tile img')) return;
    if (!once(collage, 'lpLightbox')) return;

    const visibleTiles = () =>
      [...collage.querySelectorAll('.pb-eoy-col__tile')].filter(
        (tile) => tile.querySelector('img') && tile.offsetParent,
      );

    const dialog = document.createElement('dialog');
    dialog.className = 'pb-eoy-lp-lightbox';
    dialog.setAttribute('aria-label', "Foto's");
    dialog.innerHTML = `
      <p class="pb-eoy-lp-lightbox__count" aria-live="polite"></p>
      <button type="button" class="pb-eoy-lp-lightbox__close" aria-label="Sluiten" data-lb-close>
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
      </button>
      <div class="pb-eoy-lp-lightbox__stage" data-lb-stage></div>
      <button type="button" class="pb-eoy-lp-lightbox__nav pb-eoy-lp-lightbox__nav--prev" aria-label="Vorige foto" data-lb-step="-1">
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M10 3L5 8l5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      <button type="button" class="pb-eoy-lp-lightbox__nav pb-eoy-lp-lightbox__nav--next" aria-label="Volgende foto" data-lb-step="1">
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      <div class="pb-eoy-lp-lightbox__dots" aria-hidden="true"></div>`;
    document.body.append(dialog);

    const stage = dialog.querySelector('[data-lb-stage]');
    const count = dialog.querySelector('.pb-eoy-lp-lightbox__count');
    const dots = dialog.querySelector('.pb-eoy-lp-lightbox__dots');
    let photos = [];
    let index = 0;

    const show = (next) => {
      index = (next + photos.length) % photos.length;
      const source = photos[index];
      const img = source.cloneNode();
      // Landscape photos get more room on desktop (CSS); the tile's srcset stops at 900px, so ask the CDN for more.
      const landscape = Number(source.getAttribute('width')) > Number(source.getAttribute('height'));
      const url = new URL(source.currentSrc || source.src, window.location.href);
      img.srcset = [700, 1000, 1400, 2000]
        .map((width) => {
          url.searchParams.set('width', String(width));
          return `${url.href} ${width}w`;
        })
        .join(', ');
      img.sizes = landscape ? '(min-width: 750px) min(1100px, 90vw), 90vw' : '(min-width: 750px) 520px, 90vw';
      img.loading = 'eager';
      img.className = `pb-eoy-lp-lightbox__img${landscape ? ' is-landscape' : ''}`;
      stage.replaceChildren(img);
      count.textContent = `${index + 1} / ${photos.length}`;
      dots.replaceChildren(
        ...photos.map((_, i) => {
          const dot = document.createElement('span');
          dot.classList.toggle('is-active', i === index);
          return dot;
        }),
      );
    };

    const open = (tile) => {
      const tiles = visibleTiles();
      photos = tiles.map((item) => item.querySelector('img'));
      show(Math.max(0, tiles.indexOf(tile)));
      dialog.showModal();
    };

    for (const tile of collage.querySelectorAll('.pb-eoy-col__tile')) {
      if (!tile.querySelector('img')) continue;
      tile.tabIndex = 0;
      tile.setAttribute('role', 'button');
      tile.setAttribute('aria-label', tile.querySelector('img').alt || 'Bekijk foto');
      tile.addEventListener('click', () => open(tile));
      tile.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        open(tile);
      });
    }

    dialog.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target === dialog || target.closest('[data-lb-close]')) return dialog.close();
      const step = target.closest('[data-lb-step]');
      if (step) show(index + Number(step.dataset.lbStep));
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') show(index + 1);
      if (event.key === 'ArrowLeft') show(index - 1);
    });

    // Swipe on the photo.
    let startX = null;
    stage.addEventListener('pointerdown', (event) => (startX = event.clientX));
    stage.addEventListener('pointerup', (event) => {
      if (startX === null) return;
      const dx = event.clientX - startX;
      startX = null;
      if (Math.abs(dx) > 50) show(index + (dx < 0 ? 1 : -1));
    });
  }

  window.PBEoyLp = { quizWinner };
  // Loaded without a page (tests): only the helpers above.
  if (typeof document === 'undefined') return;

  function init(scope = document) {
    scope.querySelectorAll('[data-pb-eoy-lp-hero]').forEach(initHero);
    scope.querySelectorAll('[data-lp-video]').forEach(initVideo);
    scope.querySelectorAll('[data-lp-slider]').forEach(initSlider);
    scope.querySelectorAll('[data-lp-quiz]').forEach(initQuiz);
    scope.querySelectorAll('[data-lp-dsc]').forEach(initDiscovery);
    scope.querySelectorAll('.pb-eoy-col').forEach(initLightbox);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
